import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { post } from '../../utils/api';
import { CloseX, ignoreBackdropEscapeClose } from './portalUi';
import { buildOrderLineItemLabel } from '../../utils/orderLineItemLabel';

const AUTO_CLOSE_SEC = 30;

function formatOrderTotal(order) {
  const currency = order?.currency || 'USD';
  const amount = Number(order?.total_num ?? order?.total ?? 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

/**
 * @param {{ open: boolean, onClose: () => void, order: object|null }} props
 */
export default function ReportTransactionIssueDialog({ open, onClose, order }) {
  const location = useLocation();
  const { staff } = useAuth();
  const [comment, setComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [autoCloseSec, setAutoCloseSec] = React.useState(0);
  const [msg, setMsg] = React.useState({ type: '', text: '' });
  const autoCloseTimerRef = React.useRef(null);
  const countdownRef = React.useRef(null);

  const clearAutoCloseTimers = React.useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleClose = React.useCallback(() => {
    clearAutoCloseTimers();
    onClose();
  }, [clearAutoCloseTimers, onClose]);

  React.useEffect(() => {
    if (!open) {
      clearAutoCloseTimers();
      return;
    }
    setSubmitted(false);
    setAutoCloseSec(0);
    setMsg({ type: '', text: '' });
    setComment('');
  }, [open, order?.id, clearAutoCloseTimers]);

  React.useEffect(() => {
    if (!open || !submitted) return undefined;
    setAutoCloseSec(AUTO_CLOSE_SEC);
    countdownRef.current = setInterval(() => {
      setAutoCloseSec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    autoCloseTimerRef.current = setTimeout(() => {
      handleClose();
    }, AUTO_CLOSE_SEC * 1000);
    return () => clearAutoCloseTimers();
  }, [open, submitted, handleClose, clearAutoCloseTimers]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitted || !order?.id) return;
    const trimmed = comment.trim();
    if (!trimmed) {
      setMsg({ type: 'error', text: 'Please enter your comment.' });
      return;
    }
    setSubmitting(true);
    setMsg({ type: '', text: '' });
    try {
      await post('/portal/transaction-issue', {
        orderId: order.id,
        comment: trimmed,
        pagePath: location.pathname,
      });
      setSubmitted(true);
      setMsg({
        type: 'success',
        text: 'Thank you. We received your message and sent a confirmation to your email.',
      });
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Could not send your message. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
  const orderDate = new Date(order.date_created || order.date_created_gmt).toLocaleDateString();

  return (
    <Dialog
      open={open}
      onClose={(ev, reason) => {
        if (ignoreBackdropEscapeClose(ev, reason)) return;
        if (!submitting) handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">
          Comment on transaction
        </Typography>
        <CloseX onClick={handleClose} disabled={submitting} />
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" onSubmit={onSubmit}>
          {!submitted ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a comment about this transaction below. We will email you a copy and notify our payments team.
            </Typography>
          ) : null}
          {staff?.email && !submitted ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Signed in as {staff.email}
            </Typography>
          ) : null}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Transaction details
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>Tx #:</strong> {order.id}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {orderDate}
              </Typography>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                <strong>Status:</strong> {order.status}
              </Typography>
              <Typography variant="body2">
                <strong>Total:</strong> {formatOrderTotal(order)}
              </Typography>
              {lineItems.length > 0 ? (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                    Product(s):
                  </Typography>
                  {lineItems.map((li, liIdx) => (
                    <Typography
                      key={`${order.id}-dlg-li-${liIdx}`}
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.45 }}
                    >
                      {buildOrderLineItemLabel(li)}
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Stack>
          </Box>
          <TextField
            label="Your comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            required
            multiline
            minRows={4}
            size="small"
            disabled={submitting || submitted}
            placeholder="Describe your question or comment about this transaction…"
            sx={{ mb: 1.5 }}
          />
          {msg.text ? (
            <Alert severity={msg.type === 'error' ? 'error' : 'success'} sx={{ mb: 1.5 }}>
              {msg.text}
              {submitted && autoCloseSec > 0 ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This window will close in {autoCloseSec} second{autoCloseSec === 1 ? '' : 's'}, or use Close when you are
                  done.
                </Typography>
              ) : null}
            </Alert>
          ) : null}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            {!submitted ? (
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send'}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
